import { Button, Css, DnDGrid, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { observer } from "mobx-react";
import { ConfigureBidGroupsStepQuery } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { BasePlanGroupDimensionsModal } from "src/routes/bids/plan-based-bid-package/components/BasePlanGroupDimensionsModal";
import { PersonalizationZoneCard } from "src/routes/bids/plan-based-bid-package/components/PersonalizationZoneCard";
import {
  groupKey,
  isBaseGroup,
  PlanBasedBidPackageFormState,
  reorderGroups,
} from "src/routes/bids/plan-based-bid-package/utils";

type PersonalizationZonesSectionProps = {
  formState: ObjectState<PlanBasedBidPackageFormState>;
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"];
  productOfferings: ConfigureBidGroupsStepQuery["bidPackage"]["productOfferings"];
};

export const PersonalizationZonesSection = observer(function PersonalizationZonesSection(
  props: PersonalizationZonesSectionProps,
) {
  const { formState, dimensionResults, productOfferings } = props;
  const { openModal } = useModal();
  const zoneGroups = formState.bidPlanGroups.rows
    .filter((g) => isBaseGroup(g.value))
    .sortBy((g) => g.order.value ?? Infinity);

  return (
    <div css={Css.df.fdc.gap2.mb6.$}>
      <div css={Css.df.aic.jcsb.$}>
        <div css={Css.lg.$}>Personalization Zones</div>
        <Button
          icon="plus"
          label="Add Personalization Zone"
          variant="secondary"
          onClick={() =>
            openModal({
              content: <BasePlanGroupDimensionsModal formState={formState} dimensionResults={dimensionResults} />,
            })
          }
        />
      </div>
      <div>
        Define zones within the plan that have configurable options. Cost lines populate only for product offerings that
        include the corresponding zone.
      </div>
      {zoneGroups.isEmpty ? (
        <div css={Css.bgGray100.br8.py3.tac.gray700.$}>No personalization zones added.</div>
      ) : (
        <DnDGrid
          onReorder={(newIds) =>
            formState.bidPlanGroups.set(reorderGroups(formState.bidPlanGroups.value ?? [], newIds))
          }
          gridStyles={Css.dg.gtc("minmax(0, 1fr)").gap2.$}
        >
          {zoneGroups.map((group) => (
            <PersonalizationZoneCard
              key={groupKey(group.value)}
              formState={formState}
              group={group}
              dimensionResults={dimensionResults}
              productOfferings={productOfferings}
            />
          ))}
        </DnDGrid>
      )}
    </div>
  );
});
